import React, { useState, useEffect, useMemo, useRef } from "react";
import { withPazireshAvatarThumbParam, isPazireshPicUrl } from "../utils/pazireshAvatarUrl";
import { loadPazireshPicUrl, peekPazireshPicUrl } from "../utils/pazireshAvatarLoader";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import cn from "@/common/utils/classNames";

/** Stable background color from user id (same palette as message list). */
export function getAvatarColorForUser(userId) {
  if (userId == null || userId === "") return "#737373";
  const n = typeof userId === "number" ? userId : parseInt(String(userId), 10);
  const colors = [
    "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f59e0b",
    "#10b981", "#06b6d4", "#6366f1", "#f97316", "#84cc16",
  ];
  const idx = Number.isFinite(n) ? Math.abs(n) % colors.length : Math.abs(String(userId).length) % colors.length;
  return colors[idx];
}

/** First letter of the name only (one grapheme for display in avatar circle). */
export function getAvatarInitials(fullName) {
  if (!fullName || !String(fullName).trim()) return "?";
  const s = String(fullName).trim();
  const ch = s[0] || "?";
  return ch.toUpperCase();
}

/**
 * Colored background + initials until remote image loads (or on error).
 * Pass `avatarUrl` when known; initials stay visible until `onLoad` fires.
 */
export default function UserAvatar({
  remoteId,
  fullName = "",
  avatarUrl,
  className = "",
  alt,
  imgClassName = "",
  /** e.g. '50%' (default) or '14px' for list tiles */
  borderRadius = "50%",
  style,
  ...rest
}) {
  const online = useOnlineStatus();
  const rootRef = useRef(null);
  // A face already loaded once (this page, or saved on the phone) is drawn in the first frame —
  // no letter first, no fade: Telegram never shows a known avatar arriving.
  const known = avatarUrl ? peekPazireshPicUrl(avatarUrl) : undefined;
  const [instant, setInstant] = useState(() => typeof known === "string");
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(() => known === null);
  const [displaySrc, setDisplaySrc] = useState(() => (typeof known === "string" ? known : null));
  const srcKeyRef = useRef(`${avatarUrl}|${online}`);

  const resolvedAvatarSrc = useMemo(() => {
    if (!online || !avatarUrl) return null;
    return withPazireshAvatarThumbParam(avatarUrl);
  }, [avatarUrl, online]);

  const usesPazireshLoader = Boolean(
    resolvedAvatarSrc && isPazireshPicUrl(resolvedAvatarSrc)
  );

  useEffect(() => {
    const k = `${avatarUrl}|${online}`;
    if (srcKeyRef.current === k) return; // first mount: the state above is already right
    srcKeyRef.current = k;
    const peek = avatarUrl ? peekPazireshPicUrl(avatarUrl) : undefined;
    setInstant(typeof peek === "string");
    setShouldLoad(false);
    setLoaded(false);
    setFailed(peek === null);
    setDisplaySrc(typeof peek === "string" ? peek : null);
  }, [avatarUrl, online]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !resolvedAvatarSrc || instant) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [resolvedAvatarSrc]);

  useEffect(() => {
    if (!shouldLoad || !resolvedAvatarSrc) return undefined;

    let cancelled = false;

    if (usesPazireshLoader) {
      loadPazireshPicUrl(resolvedAvatarSrc).then((src) => {
        if (cancelled) return;
        if (src) {
          setDisplaySrc(src);
        } else {
          setFailed(true);
          setLoaded(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    setDisplaySrc(resolvedAvatarSrc);
    return undefined;
  }, [shouldLoad, resolvedAvatarSrc, usesPazireshLoader]);

  const bg = getAvatarColorForUser(remoteId);
  const initials = getAvatarInitials(fullName);
  const showInitials = !displaySrc || failed || (!loaded && !instant);
  const safeAlt = alt ?? fullName ?? "";

  return (
    <div
      ref={rootRef}
      className={cn("user-avatar-root relative block shrink-0 overflow-hidden box-border", instant && "bg-slate-200", className)}
      style={{ borderRadius, ...style }}
      {...rest}
    >
      <span
        className="user-avatar-initials-layer pointer-events-none absolute inset-0 z-0 flex items-center justify-center rounded-[inherit] font-semibold text-white transition-opacity duration-200 [font-size:inherit]"
        aria-hidden
        style={{ backgroundColor: bg, opacity: showInitials ? 1 : 0, transition: instant ? "none" : undefined }}
      >
        {initials}
      </span>
      {displaySrc && !failed && (
        <img
          src={displaySrc}
          alt={safeAlt}
          className={cn("user-avatar-img-layer pointer-events-none absolute inset-0 z-[1] h-full w-full rounded-[inherit] object-cover transition-opacity duration-200", imgClassName)}
          style={instant ? { opacity: 1, transition: "none" } : { opacity: loaded ? 1 : 0 }}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setInstant(false);
            setFailed(true);
            setLoaded(false);
          }}
        />
      )}
    </div>
  );
}
